import type { RuleArea } from "@unbox/api";
import { Package, Tag01, UserX01 } from "@untitledui/icons";

type Starter = {
  area: RuleArea;
  title: string;
  subtitle: string;
  icon: typeof Tag01;
  image: string;
  imageClassName: string;
};

const STARTERS: Starter[] = [
  {
    area: "sample",
    title: "Auto Approve Samples",
    subtitle: "Use Unbox data to auto approve sample requests",
    icon: Package,
    image: "/rules/starter-samples.webp",
    imageClassName: "-top-20 -right-32 h-[250px]",
  },
  {
    area: "cohort",
    title: "Create Cohorts",
    subtitle: "Dynamic cohorts based on your rules that update every month",
    icon: Tag01,
    image: "/rules/starter-cohorts.webp",
    imageClassName: "-top-12 -right-14 h-[300px]",
  },
  {
    area: "blocklist",
    title: "Block Creators",
    subtitle: "Creators without samples are rejected and their videos removed",
    icon: UserX01,
    image: "/rules/starter-blocked.webp",
    imageClassName: "-top-24 -right-16 h-[460px]",
  },
];

export const StarterRules = ({ onStart }: { onStart: (area: RuleArea) => void }) => (
  <section className="flex flex-col gap-4 flex-shrink-0">
    <h2 className="text-base font-medium text-primary">Starter Rules</h2>
    <div className="grid gap-4 md:grid-cols-3">
      {STARTERS.map((starter) => (
        <div
          key={starter.area}
          className="relative isolate flex flex-col gap-5 overflow-hidden rounded-xl bg-linear-to-b from-unbox-dark-brand-start to-unbox-dark-brand-end p-4"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-2/3 bg-linear-to-b from-brand-700/55 to-transparent"
          />
          <img
            aria-hidden="true"
            alt=""
            src={starter.image}
            className={`pointer-events-none absolute -z-10 max-w-none opacity-40 brightness-90 mix-blend-luminosity ${starter.imageClassName}`}
          />
          <div className="flex items-start gap-2.5">
            <starter.icon className="mt-0.5 size-5 shrink-0 text-brand-100" strokeWidth={1.8} aria-hidden="true" />
            <div className="flex min-w-0 flex-col gap-1">
              <h3 className="text-[15px] leading-6 font-medium text-white">{starter.title}</h3>
              <p className="text-[13px] leading-[18px] text-brand-100/80">{starter.subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            aria-label={`Create rule: ${starter.title}`}
            className="mt-auto w-full cursor-pointer rounded-lg bg-brand-100 px-4 py-2.5 text-sm font-semibold text-brand-900 transition-colors duration-100 hover:bg-brand-200"
            onClick={() => onStart(starter.area)}
          >
            Create rule
          </button>
        </div>
      ))}
    </div>
  </section>
);
